import React, {ChangeEvent} from 'react';

import {CallForPapers, FormWidget, PaperSubmission, Theme} from '../../types/types';
import {fromPairs} from "lodash";
import {Form} from "react-bootstrap";
import {FormikValues, useField, useFormikContext} from "formik";
import * as yup from "yup";
import {useIntl} from "react-intl";
import I18nText from "../../i18n/I18nText";

interface ThemesSelectWidgetProps {
    widget: FormWidget;
    callForPapers: CallForPapers;
    disabled: boolean;
}

/**
 * Widget für die Auswahl mehrer Themen für eine Einreichung.
 *
 * @param widget
 * @param callForPapers
 * @param paperSubmission
 * @param disabled
 * @constructor
 */
const ThemesSelectWidget: React.FC<ThemesSelectWidgetProps> = ({widget, callForPapers, disabled}) => {

    const FIELD_NAME = `widgets.${widget.id}`;

    const intl = useIntl();
    const {handleBlur}: FormikValues = useFormikContext()
    const [field, meta, helpers] = useField<Theme[]>(FIELD_NAME);

    const availableThemes: Theme[] = callForPapers?.themes ?? [];
    const selectedThemes: Record<string, boolean> = fromPairs(field.value?.map((theme) => [theme.id, true]));

    /**
     * @param event
     * @param theme
     */
    const handleChange = (event: ChangeEvent<HTMLInputElement>, theme: Theme) => {
        const modifiedSelectedThemes = {...selectedThemes, [theme.id]: event.currentTarget.checked}

        helpers.setValue(availableThemes.filter((t) => modifiedSelectedThemes[t.id]));
    };

    return (
        <Form.Group>
            <Form.Label>
                <I18nText content={widget.label}/>
            </Form.Label>
            {availableThemes.map((theme: Theme, index: number) => (
                // Es muss der index als key genommen werden, da nicht garantiert ist,
                // dass label oder value der choice eindeutig sind.
                <Form.Check type="checkbox"
                            id={`${FIELD_NAME}-${index}`}
                            name={`${FIELD_NAME}-${index}`}
                            key={index}
                            label={theme.label?.[intl.locale] ?? ''}
                            onChange={(event) => handleChange(event, theme)}
                            onBlur={handleBlur}
                            checked={!!selectedThemes[theme.id]}
                            disabled={disabled}
                            data-testid={widget.id}
                />
            ))}
            <Form.Control.Feedback type="invalid">
                {meta.error}
            </Form.Control.Feedback>
        </Form.Group>
    );
}

export function type() {
    return 'themes_select';
}

export function initialValue(widget: FormWidget) {
    return [];
}

export function submissionValue(callForPapers: CallForPapers, widget: FormWidget, submission: PaperSubmission) {
    if (!submission.themes) {
        return [];
    }

    const themeIds = new Set(submission.themes?.map(t => t.id));

    return callForPapers.themes.filter(t => themeIds.has(t.id));
}

export function schema(widget: FormWidget) {
    return yup.array()
        .of(yup.object())
        .strict(true)
        .nullable();
}

export function component() {
    return ThemesSelectWidget;
}
